/* =====================================================================
   Balleh — main theme JS
   Cart drawer · search overlay · mobile menu · qty steppers · mega menu
   ===================================================================== */
(function () {
  'use strict';

  const Balleh = window.Balleh || {};
  const $  = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  /* ───────── i18n · money · toast helpers ───────── */
  const tr = (key) => (Balleh.strings && Balleh.strings[key]) || key;
  function formatMoney(cents) {
    // SAR-only store: mirror `money_without_currency` (comma thousands, 2 decimals).
    const n = (Number(cents) || 0) / 100;
    return n.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  let toastEl = null;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    clearTimeout(toastEl._timer);
    toastEl._timer = setTimeout(() => toastEl.classList.remove('is-visible'), 4000);
  }

  /* ───────── Focus trap — keeps Tab inside an open dialog (paired with aria-modal) ───────── */
  let activeTrap = null;
  function focusables(container) {
    return $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container)
      .filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
  }
  function trapFocus(container) {
    releaseFocus();
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = focusables(container);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!container.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
      // Focus starts on the dialog itself (see below), which sits before every item —
      // without this, shift+Tab from there would walk straight out of the trap.
      if (document.activeElement === container) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    activeTrap = { container, handler };
    // Focus the dialog, not its first link. Programmatic focus on a link paints a
    // focus ring that a touch user never asked for — it was left sitting on the menu
    // logo and on the cart/filter drawer close buttons on every open.
    container.setAttribute('tabindex', '-1');
    container.focus({ preventScroll: true });
  }
  function releaseFocus() {
    if (!activeTrap) return;
    activeTrap.container.removeEventListener('keydown', activeTrap.handler);
    activeTrap = null;
  }

  /* ───────── Drawer / overlay helpers ───────── */
  const scrim = $('[data-scrim]');
  let overlayOpener = null;
  function openOverlay(el) {
    overlayOpener = document.activeElement;
    el.setAttribute('aria-hidden', 'false');
    el.removeAttribute('inert');               // restore focusability while open
    // The mobile menu carries its OWN full-screen backdrop (.mobile-menu) and lives inside
    // the header section's z-index:30 stacking context. The shared root-level scrim (z-index:40)
    // would paint ABOVE the trapped menu and swallow every click. Only raise the shared scrim
    // for drawers that rely on it (cart / search), which sit at root z-index:50 above it.
    if (scrim && !el.matches('[data-mobile-menu]')) scrim.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    trapFocus(el);                             // move focus in + keep Tab inside the dialog
  }
  function closeAllOverlays() {
    let any = false;
    for (const el of $$('[aria-hidden="false"][data-cart-drawer], [aria-hidden="false"][data-search-overlay], [aria-hidden="false"][data-mobile-menu], [aria-hidden="false"][data-quickview-modal]')) {
      any = true;
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');          // pull focusable descendants out of the tab order when closed
    }
    if (scrim) scrim.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (any) {
      releaseFocus();
      if (overlayOpener && overlayOpener.focus) overlayOpener.focus();  // restore focus to the trigger
      overlayOpener = null;
    }
  }
  if (scrim) scrim.addEventListener('click', closeAllOverlays);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAllOverlays(); });

  /* ───────── Cart drawer ───────── */
  const cartDrawer = $('[data-cart-drawer]');

  document.addEventListener('click', (e) => {
    const pwToggle = e.target.closest('[data-password-toggle]');
    if (pwToggle) {
      // Reveal / hide the password (snippets/password-field.liquid). Keep the caret
      // where it was — switching `type` moves it to the end in some browsers.
      e.preventDefault();
      const field = document.getElementById(pwToggle.getAttribute('data-password-toggle'));
      if (!field) return;
      const show = field.type === 'password';
      const pos = field.selectionStart;
      field.type = show ? 'text' : 'password';
      try { field.setSelectionRange(pos, pos); } catch (_) { /* type=text only */ }
      pwToggle.setAttribute('aria-pressed', String(show));
      const label = show ? 'hide' : 'show';
      const txt = pwToggle.getAttribute('data-label-' + label);
      if (txt) { pwToggle.setAttribute('aria-label', txt); pwToggle.setAttribute('title', txt); }
      const shown = pwToggle.querySelector('[data-icon-show]');
      const hidden = pwToggle.querySelector('[data-icon-hide]');
      if (shown) shown.hidden = show;
      if (hidden) hidden.hidden = !show;
      field.focus();
      return;
    }
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openCart();
    } else if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeAllOverlays();
    } else if (e.target.closest('[data-discount-apply]')) {
      e.preventDefault();
      const codeInput = $('[data-discount-input]');
      const code = (codeInput && codeInput.value || '').trim();
      if (!code) { if (codeInput) codeInput.focus(); return; }
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      location.href = root + 'discount/' + encodeURIComponent(code) + '?redirect=/cart';
    } else if (e.target.closest('[data-search-open]')) {
      e.preventDefault();
      openSearch();
    } else if (e.target.closest('[data-search-close]')) {
      e.preventDefault();
      closeAllOverlays();
    } else if (e.target.closest('[data-mobile-menu-open]')) {
      e.preventDefault();
      openMobileMenu();
    } else if (e.target.closest('[data-mobile-menu-close]')) {
      e.preventDefault();
      closeAllOverlays();
    } else if (e.target.matches('[data-mobile-menu]')) {
      // Click landed on the drawer's own dark backdrop (the .mobile-menu element
      // itself, outside .mobile-menu__panel) — close it, like the other drawers' scrim.
      closeAllOverlays();
    } else if (e.target.closest('[data-submenu-toggle]')) {
      e.preventDefault();
      toggleSubmenu(e.target.closest('[data-submenu-toggle]'));
    }
  });

  /* ───────── Auth form validation (login + register) ─────────
     Forms render novalidate. Every [data-validate] field is checked client-side: errors
     surface on blur ([data-error]) and again on submit, which is blocked while anything
     is invalid. The button is never disabled — browser/password-manager autofill doesn't
     always fire input events, and a dead "sign in" button is worse than a validation
     message. On submit the Saudi phone ([data-sa-phone]) is normalized to E.164. */
  const AUTH_FORMS = '#create_customer, #customer_login';
  const authChecks = {
    required: (v, el) => (el.type === 'checkbox' ? el.checked : v.trim() !== ''),
    email:    (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
    min5:     (v) => v.length >= 5,
    saphone:  (v) => /^5\d{8}$/.test(saPhoneDigits(v)),
    // Confirmation fields carry data-match="<id of the field to equal>".
    match:    (v, el) => { const t = document.getElementById(el.getAttribute('data-match')); return !!t && v === t.value; },
    checked:  (v, el) => el.checked,
  };
  function saPhoneDigits(v) {
    let d = (v || '').replace(/\D/g, '');
    if (d.indexOf('966') === 0) d = d.slice(3);
    if (d.indexOf('0') === 0) d = d.slice(1);
    return d;
  }
  function authFieldValid(input) {
    const checks = (input.getAttribute('data-validate') || '').split(/\s+/);
    return checks.every((c) => !authChecks[c] || authChecks[c](input.value, input));
  }
  function setFieldError(input, msg) {
    input.setAttribute('aria-invalid', 'true');
    const field = input.closest('.field') || input.parentNode;
    let el = field.querySelector('.field__error');
    if (!el) {
      el = document.createElement('small');
      el.className = 'field__error';
      el.setAttribute('role', 'alert');
      field.appendChild(el);
    }
    el.textContent = msg;
  }
  function clearFieldError(input) {
    input.removeAttribute('aria-invalid');
    const field = input.closest('.field');
    const el = field && field.querySelector('.field__error');
    if (el) el.textContent = '';
  }
  // Live: clear a field's error as soon as the user edits it.
  document.addEventListener('input', (e) => {
    const form = e.target.closest && e.target.closest(AUTH_FORMS);
    if (!form || !e.target.matches('[data-validate]')) return;
    if (e.target.matches('[data-sa-phone]')) {
      // Keep the visible field as the LOCAL number only — the +966 prefix is fixed in the
      // UI, so strip any country code / non-digits and never show a second +966.
      let d = e.target.value.replace(/\D/g, '');
      if (d.indexOf('966') === 0) d = d.slice(3);
      d = d.slice(0, 10);
      if (e.target.value !== d) e.target.value = d;
    }
    clearFieldError(e.target);
  });
  // Show a field's error once the user leaves it invalid.
  document.addEventListener('focusout', (e) => {
    if (!e.target.matches || !e.target.matches('[data-validate]') || !e.target.closest(AUTH_FORMS)) return;
    if (authFieldValid(e.target)) clearFieldError(e.target);
    else setFieldError(e.target, e.target.getAttribute('data-error') || '');
  });
  // Submit: block while invalid (show every error, focus the first) + phone normalization.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches || !form.matches(AUTH_FORMS)) return;
    let firstInvalid = null;
    for (const input of form.querySelectorAll('[data-validate]')) {
      if (authFieldValid(input)) clearFieldError(input);
      else { setFieldError(input, input.getAttribute('data-error') || ''); if (!firstInvalid) firstInvalid = input; }
    }
    if (firstInvalid) { e.preventDefault(); firstInvalid.focus(); return; }
    // Submit the full E.164 number via the HIDDEN field; never rewrite the visible field
    // (it shows only the local part, since the +966 prefix is fixed in the UI).
    const phone = form.querySelector('[data-sa-phone]');
    const phoneTarget = form.querySelector('[data-sa-phone-target]');
    if (phoneTarget) phoneTarget.value = (phone && phone.value) ? '+966' + saPhoneDigits(phone.value) : '';
  });

  function openCart()        { if (cartDrawer) openOverlay(cartDrawer); }
  function openSearch()      { const el = $('[data-search-overlay]'); if (el) openOverlay(el); }
  function openMobileMenu()  { const el = $('[data-mobile-menu]');   if (el) openOverlay(el); }
  function toggleSubmenu(btn) {
    const panel  = document.getElementById(btn.getAttribute('aria-controls'));
    const isOpen = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!isOpen));
    if (panel) panel.hidden = isOpen;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function fetchSection(sectionId) {
    const url = `/?section_id=${sectionId}`;
    const res = await fetch(url);
    const text = await res.text();
    return text;
  }

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const res = await fetch('?section_id=cart-drawer', { headers: { Accept: 'text/html' } });
      if (!res.ok) {
        // Fallback — re-render via /cart and replace innerHTML
        const cart = await fetchCart();
        renderCartSummary(cart);
        return;
      }
      const html = await res.text();
      // The endpoint returns the full section wrapper including the <aside class="cart-drawer">.
      // We must extract the INNER content of that aside (header + list + foot), not the aside itself —
      // otherwise we end up nesting an <aside class="cart-drawer"> inside the existing one, and the
      // inner one keeps its position:fixed off-screen transform, hiding the real content.
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('[data-cart-drawer]');
      if (newDrawer) {
        cartDrawer.innerHTML = newDrawer.innerHTML;
      } else {
        const fallback = doc.querySelector('.shopify-section') || doc.body;
        if (fallback) cartDrawer.innerHTML = fallback.innerHTML;
      }
    } catch (err) {
      const cart = await fetchCart();
      renderCartSummary(cart);
    }
  }

  function renderCartSummary(cart) {
    // Minimal client-side fallback if section fetch fails
    const count = $('[data-cart-count]');
    if (count) {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    }
  }

  function updateBadge(itemCount) {
    for (const el of $$('[data-cart-count]')) {
      el.textContent = itemCount;
      el.hidden = itemCount === 0;
    }
  }

  /* ───────── Buy now — capture phase, app-proof ─────────
     Some stores run bundle/upsell apps (e.g. Revy) that bind their own handler to the
     buy-now button and jump to /checkout WITHOUT adding the product, so checkout opens on
     an empty cart and bounces back to the homepage. We catch the click in the CAPTURE
     phase — before any app's (bubble-phase) handler — stop it reaching them, and submit a
     native POST to /cart/add with return_to=/checkout. form.submit() fires no click/submit
     event, so app listeners never see it; the server adds the item and 302s to checkout in
     one request nothing can short-circuit. Works with or without such an app. */
  let buyNowBusy = false;
  // bfcache: coming Back from checkout restores this script's state, so re-arm the guard.
  window.addEventListener('pageshow', () => { buyNowBusy = false; });
  window.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('button[name="checkout"], [data-buy-now]');
    if (!btn) return;
    const form = btn.closest('[data-product-form]');
    if (!form) return;

    e.preventDefault();
    e.stopImmediatePropagation();        // keep bundle/upsell apps from also handling this click
    if (buyNowBusy) return;
    buyNowBusy = true;

    const relay = document.createElement('form');
    relay.method = 'post';
    relay.action = form.getAttribute('action') || (Balleh.routes && Balleh.routes.cart_add_url) || '/cart/add';
    relay.style.display = 'none';
    const data = new FormData(form);             // excludes submit buttons; carries id, quantity, properties
    data.set('return_to', '/checkout');
    for (const pair of data.entries()) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = pair[0];
      input.value = pair[1];
      relay.appendChild(input);
    }
    document.body.appendChild(relay);
    relay.submit();
  }, true);

  /* ───────── Add to cart (forms with data-product-form) ───────── */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;

    // "Buy now" (submitter name="checkout") submits the form NATIVELY to /cart/add with a
    // return_to=/checkout, so the server adds the item and 302-redirects to checkout in a
    // single request. The old path AJAX-added then set window.location='/checkout', which
    // raced the cart cookie on Chrome for Android and bounced the shopper back to the
    // homepage before checkout finished loading. Plain add-to-cart still AJAX-adds and
    // opens the drawer.
    const submitter = e.submitter;
    if (submitter && submitter.name === 'checkout') {
      let returnTo = form.querySelector('input[name="return_to"]');
      if (!returnTo) {
        returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        form.appendChild(returnTo);
      }
      returnTo.value = '/checkout';
      return; // do NOT preventDefault — let the browser submit natively
    }

    e.preventDefault();

    const button = submitter || form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = true;
      // Save innerHTML (not textContent) so any icon markup inside the button
      // — e.g. the cart glyph on product-card CTAs — is restored intact afterwards.
      button.dataset.originalHtml = button.innerHTML;
      button.textContent = '…';
    }

    try {
      const formData = new FormData(form);
      const res = await fetch(Balleh.routes.cart_add_url, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/javascript' }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((typeof data.errors === 'string' && data.errors) || data.description || tr('cart.error_generic'));

      const cart = await fetchCart();
      updateBadge(cart.item_count);

      await refreshCartDrawer();
      openCart();
    } catch (err) {
      console.error(err);
      toast(err.message || tr('cart.error_generic'));
    } finally {
      if (button) {
        button.disabled = false;
        button.innerHTML = button.dataset.originalHtml || tr('product.add_to_cart');
      }
    }
  });

  /* ───────── Newsletter → Klaviyo (forms with data-newsletter-klaviyo) ───────── */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-newsletter-klaviyo]');
    if (!form) return;
    e.preventDefault();

    const company = form.dataset.klaviyoCompany;
    const listId  = form.dataset.klaviyoList;
    const input   = form.querySelector('input[type="email"]');
    const button  = form.querySelector('button[type="submit"]');
    const msg     = form.querySelector('[data-newsletter-message]');
    const email   = ((input && input.value) || '').trim();

    const show = (text, type) => {
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = false;
      msg.className = 'newsletter__message newsletter__message--' + type;
    };

    // Validate on the client so the bubble shows the Arabic message (the form is novalidate).
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      show(form.dataset.msgInvalid, 'error');
      if (input) input.focus();
      return;
    }

    const original = button ? button.textContent : '';
    if (button) { button.disabled = true; button.textContent = form.dataset.msgSubmitting || '…'; }

    try {
      const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Balleh Newsletter',
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      });
      // Klaviyo returns 202 Accepted on success (empty body).
      if (res.status === 202 || res.ok) {
        show(form.dataset.msgSuccess, 'success');
        form.reset();
      } else {
        show(res.status === 400 ? form.dataset.msgInvalid : form.dataset.msgError, 'error');
      }
    } catch (err) {
      console.error('Klaviyo subscribe failed:', err);
      show(form.dataset.msgError, 'error');
    } finally {
      if (button) { button.disabled = false; button.textContent = original; }
    }
  });

  /* ───────── Cart line item controls (drawer + cart page) ───────── */
  // Line updates run one at a time. Each +/- reads the quantity when it EXECUTES (after the
  // previous update re-rendered the drawer), so two fast taps give +2, not +1 twice.
  let cartQueue = Promise.resolve();
  const queueCartUpdate = (job) => { cartQueue = cartQueue.then(job, job); return cartQueue; };

  document.addEventListener('click', (e) => {
    const inc = e.target.closest('[data-cart-increase]');
    const dec = e.target.closest('[data-cart-decrease]');
    const rem = e.target.closest('[data-cart-remove]');
    if (!inc && !dec && !rem) return;

    e.preventDefault();
    const key = (inc || dec || rem).getAttribute(inc ? 'data-cart-increase' : dec ? 'data-cart-decrease' : 'data-cart-remove');
    queueCartUpdate(() => {
      if (rem) return updateCartLine(key, 0);
      const input = document.querySelector(`[data-cart-input="${key}"]`);
      if (!input) return;                       // line already gone (removed by an earlier queued update)
      const current = parseInt(input.value, 10) || 0;
      return updateCartLine(key, inc ? current + 1 : Math.max(0, current - 1));
    });
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-input]');
    if (!input) return;
    const key = input.getAttribute('data-cart-input');
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    queueCartUpdate(() => updateCartLine(key, qty));
  });

  async function updateCartLine(key, quantity) {
    try {
      const res = await fetch(Balleh.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json().catch(() => ({}));
      if (!res.ok) {
        // e.g. 422 "only N in stock" — already in the shop language. routes.cart_change_url
        // (/cart/change) answers {errors: "…"}; the .js endpoint answers {description: "…"}.
        const msg = cart.description || cart.message || cart.errors;
        toast(typeof msg === 'string' ? msg : tr('cart.error_generic'));
        // Cart PAGE inputs aren't re-rendered by the drawer refresh — snap them back by hand.
        const fresh = await fetchCart();
        const line = fresh.items.find((i) => i.key === key);
        for (const el of $$(`[data-cart-input="${key}"]`)) el.value = line ? line.quantity : 0;
      } else {
        updateBadge(cart.item_count);
      }
      await refreshCartDrawer();               // also snaps a rejected quantity back to the real one
      // If we're on the cart page, reload to re-render server-rendered totals
      if (res.ok && document.body.classList.contains('template-cart')) {
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
      toast(tr('cart.error_generic'));
    }
  }

  /* ───────── Qty steppers (PDP, not cart) ───────── */
  document.addEventListener('click', (e) => {
    const inc = e.target.closest('[data-qty-increase]');
    const dec = e.target.closest('[data-qty-decrease]');
    if (!inc && !dec) return;
    const wrap = (inc || dec).closest('.qty, .pdp__qty');
    const input = wrap && wrap.querySelector('[data-qty-input], .qty__num');
    if (!input) return;
    let v = parseInt(input.value, 10) || 1;
    const max = parseInt(input.max, 10) || 99;
    if (inc) v = Math.min(max, v + 1);
    if (dec) v = Math.max(1, v - 1);
    input.value = v;
    // Sync to hidden quantity input on PDP
    const form = wrap.closest('form');
    if (form) {
      const qi = form.querySelector('[data-quantity-input]');
      if (qi) qi.value = v;
    }
  });

  // Typing directly into the PDP qty box (not just +/-): clamp to [1..max] and
  // mirror into the hidden submitted field, otherwise the form posts quantity=1.
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const max = parseInt(input.max, 10) || 99;
    let v = parseInt(input.value, 10);
    if (!v || v < 1) v = 1;
    v = Math.min(max, v);
    input.value = v;
    const form = input.closest('form');
    if (form) {
      const qi = form.querySelector('[data-quantity-input]');
      if (qi) qi.value = v;
    }
  });

  /* ───────── Mega menu — pure CSS hover/focus, no JS needed ───────── */
  /* Previously this section attached mouseenter/mouseleave handlers to add an
     .is-open class. That caused the panel to close when the cursor crossed the
     gap between the parent <li> and the absolutely-positioned panel below.
     Switched to a pure-CSS :hover + :focus-within chain in theme.css with a
     ::before hover-bridge to eliminate the gap. No JS required. */

  /* ───────── Sort by ───────── */
  const sortBy = $('[data-sort-by]');
  if (sortBy) {
    sortBy.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortBy.value);
      window.location.href = url.toString();
    });
  }

  /* ───────── Accordion ─────────
     Native <details>/<summary> (sections/main-product.liquid) — works without
     JS, so no toggle handler is needed here. */

  /* ───────── Sticky header ─────────
     The header is sticky via CSS (the .shopify-section wrapper around .site-header).
     Its top offset is var(--ann-bar-h) so it sits beneath the announcement bar —
     see initAnnouncementBar below, which publishes that height and drives the
     hide-on-scroll behavior. No JS runs here for the header itself. */

  /* ───────── Per-section widgets ─────────
     Gallery, counters and the brands carousel bind to specific elements (not via
     document-level delegation), so they must re-init when the Theme Editor swaps a
     section's DOM. initDynamic(root) scopes its queries to `root` — document on first
     load, or the reloaded section on `shopify:section:load` — so existing widgets are
     never double-bound. A WeakSet marks already-initialised roots as a belt-and-braces
     guard against re-binding the same element twice. */
  const dynamicInit = new WeakSet();

  function initGallery(root) {
    const galleryThumbs = $$('[data-gallery-thumb]', root);
    const galleryMain = $('[data-gallery-main-img]', root);
    const galleryDataEl = $('[data-gallery-data]', root);
    if (!galleryThumbs.length) return;
    let galleryData = [];
    if (galleryDataEl) {
      try { galleryData = JSON.parse(galleryDataEl.textContent); } catch (e) {}
    }
    for (const thumb of galleryThumbs) {
      if (dynamicInit.has(thumb)) continue;
      dynamicInit.add(thumb);
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.getAttribute('data-gallery-thumb'), 10);
        for (const th of galleryThumbs) { th.classList.remove('is-active'); th.setAttribute('aria-current', 'false'); }
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-current', 'true');
        if (galleryMain && galleryData[idx]) {
          // The main image ships with a srcset for the LCP image; while present it
          // overrides src, so the picture would never change. Clear it before swapping.
          galleryMain.removeAttribute('srcset');
          galleryMain.removeAttribute('sizes');
          galleryMain.src = galleryData[idx].src;
          galleryMain.alt = galleryData[idx].alt || '';
        }
      });
    }
  }

  function initCounters(root) {
    if (!('IntersectionObserver' in window)) return;
    const counters = $$('[data-counter-to]', root).filter((c) => !dynamicInit.has(c));
    if (!counters.length) return;
    const counterIO = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        counterIO.unobserve(el);
        const target = parseInt(el.getAttribute('data-counter-to'), 10) || 0;
        const prefix = el.getAttribute('data-counter-prefix') || '';
        const suffix = el.getAttribute('data-counter-suffix') || '';
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce || target <= 0) {
          el.textContent = prefix + target + suffix;
        } else {
          // rAF time-based count-up — frame-aligned (no 10ms timer storm) and
          // capped at ~1.2s regardless of target, so big numbers don't run long.
          const duration = 1200;
          let startTs = 0;
          const step = (ts) => {
            if (!startTs) startTs = ts;
            const progress = Math.min((ts - startTs) / duration, 1);
            el.textContent = prefix + Math.round(progress * target) + suffix;
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      }
    }, { threshold: 0.25 });
    for (const c of counters) { dynamicInit.add(c); counterIO.observe(c); }
  }

  function initBrandsCarousel(root) {
    for (const shell of $$('[data-brands-shell]', root)) {
      if (dynamicInit.has(shell)) continue;
      const track    = $('[data-brands-track]', shell);
      const navStart = $('[data-brands-nav="start"]', shell);
      const navEnd   = $('[data-brands-nav="end"]', shell);
      if (!track || (!navStart && !navEnd)) continue;
      dynamicInit.add(shell);

      const isRTL = getComputedStyle(track).direction === 'rtl';

      const refresh = () => {
        const overflowing = track.scrollWidth - track.clientWidth > 4;
        for (const b of [navStart, navEnd]) { if (b) b.hidden = !overflowing; }
        if (!overflowing) return;
        // scrollLeft is 0 at start in both LTR and modern RTL ("negative-zero" mode),
        // and grows to ±max at end. |scrollLeft| therefore ranges 0 → max in both.
        const abs = Math.abs(track.scrollLeft);
        const max = track.scrollWidth - track.clientWidth;
        if (navStart) navStart.disabled = abs < 2;
        if (navEnd)   navEnd.disabled   = max - abs < 2;
      };

      const step = (direction /* -1 = start, +1 = end (source order) */) => {
        // In RTL, scrolling toward source-end means decreasing scrollLeft (going
        // more negative), so flip the physical sign.
        const physical = isRTL ? -direction : direction;
        track.scrollBy({ left: physical * Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
      };

      if (navStart) navStart.addEventListener('click', () => step(-1));
      if (navEnd)   navEnd.addEventListener('click',   () => step(+1));
      track.addEventListener('scroll', refresh, { passive: true });
      window.addEventListener('resize', refresh);
      // Images may load after first paint and change scrollWidth → re-check.
      for (const img of $$('img', track)) {
        if (img.complete) continue;
        img.addEventListener('load', refresh, { once: true });
      }
      refresh();
    }
  }

  /* ───────── Announcement bar ─────────
     Sticky strip above the header. Publishes its height to --ann-bar-h on <html>
     so the sticky header sits flush beneath it. data-scroll-behavior:
       "fixed" — always pinned;
       "hide"  — slides up on scroll-down, returns on scroll-up (and --ann-bar-h
                 drops to 0 so the header rises to fill the top). */
  let annCleanup = null;
  function initAnnouncementBar() {
    // Always query the whole document, never just the reloaded section: a merchant
    // can stack several bars, and each one has to know the height of the bars above
    // it. Selecting only the first left every bar stuck at top:0, overlapping.
    const bars = $$('[data-announcement-bar]');
    if (!bars.length) return;
    if (annCleanup) annCleanup();           // drop listeners from a prior init (Theme Editor)

    const rootEl = document.documentElement;
    // Sticky lives on the .shopify-section wrapper (see theme.css) — that's what
    // carries the offset. Stack the bars, then publish the total so the header
    // sticks flush beneath the lot.
    const publishHeight = () => {
      let offset = 0;
      for (const bar of bars) {
        bar.parentElement.style.insetBlockStart = offset + 'px';
        // How far this bar sits below the viewport top once stuck. The hide transform
        // adds it so a hidden bar clears the bars above it instead of landing on them.
        bar.style.setProperty('--ann-stack-top', offset + 'px');
        if (!bar.classList.contains('is-hidden')) offset += bar.offsetHeight;
      }
      rootEl.style.setProperty('--ann-bar-h', offset + 'px');
    };
    publishHeight();
    window.addEventListener('resize', publishHeight, { passive: true });

    let onScroll = null;
    const hiders = bars.filter((b) => b.getAttribute('data-scroll-behavior') === 'hide');
    if (hiders.length) {
      let lastY = window.scrollY;
      let ticking = false;
      onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const y = Math.max(0, window.scrollY);
          if (Math.abs(y - lastY) > 4) {
            // Hide once scrolled past the bar; reveal on any upward scroll.
            for (const bar of hiders) {
              if (y > lastY && y > bar.offsetHeight) bar.classList.add('is-hidden');
              else if (y < lastY) bar.classList.remove('is-hidden');
            }
            publishHeight();
            lastY = y;
          }
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    annCleanup = () => {
      window.removeEventListener('resize', publishHeight);
      if (onScroll) window.removeEventListener('scroll', onScroll);
    };
  }

  /* ───────── WhatsApp float ─────────
     A fixed corner button on a 390px screen has nowhere to sit that isn't over the
     content column (the gutter is 16px), so it was landing on the login/register
     submit buttons, PDP thumbnails and product-card CTAs. Tuck it away while the
     shopper scrolls down toward a button; bring it back on any upward scroll.
     Phones only — the CSS class is inside a max-width media query. */
  function initFloatingControls() {
    // Both floats live in the same band above the bottom nav and both sit over the
    // single content column — on a 390px screen the gutter is 16px, so there is
    // nowhere for them to go. Tuck them while the shopper scrolls down toward
    // something, and whenever a field has focus (the form owns the screen then,
    // and the on-screen keyboard has already taken half of it).
    const floats = $$('[data-wa-float], [data-collection-fab]');
    if (!floats.length) return;
    const setTucked = (on) => floats.forEach((el) => el.classList.toggle('is-tucked', on));
    // Also drives the bottom tab bar out of the way while typing (theme.css).
    const setTyping = (on) => document.body.classList.toggle('is-field-focused', on);

    let lastY = window.scrollY;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = Math.max(0, window.scrollY);
        if (Math.abs(y - lastY) > 6) {
          if (!document.querySelector(':focus-within input, :focus-within textarea')) {
            setTucked(y > lastY && y > 120);
          }
          lastY = y;
        }
        ticking = false;
      });
    }, { passive: true });

    const isField = (el) => !!(el && el.matches && el.matches('input:not([type="hidden"]), textarea, select'));
    document.addEventListener('focusin', (e) => { if (isField(e.target)) { setTucked(true); setTyping(true); } });
    document.addEventListener('focusout', () => {
      // The timeout rides out field-to-field tabbing so the controls don't flicker.
      setTimeout(() => {
        if (!isField(document.activeElement)) { setTucked(false); setTyping(false); }
      }, 80);
    });
  }
  initFloatingControls();

  function initDynamic(root) {
    initGallery(root);
    initCounters(root);
    initBrandsCarousel(root);
    initAnnouncementBar(); // always re-reads every bar in the document
  }

  initDynamic(document);
  // Re-init the swapped section when a merchant edits it in the Theme Editor.
  document.addEventListener('shopify:section:load', (e) => initDynamic(e.target));

  /* ───────── Product recommendations (Section Rendering API) ─────────
     The `recommendations` object only returns products when the section is fetched
     through the Product Recommendations + Section Rendering API, so the server-side
     render is empty. We fetch the populated section here and hydrate it in place. */
  const recoSection = $('[data-product-recommendations]');
  if (recoSection && recoSection.getAttribute('data-section-url')) {
    (async () => {
      try {
        const res = await fetch(recoSection.getAttribute('data-section-url'));
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-product-recommendations]');
        if (fresh) recoSection.innerHTML = fresh.innerHTML;
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    })();
  }

  /* ───────── Localize Shopify's built-in policy page title (English → Arabic) ─────────
     Shop policies (/policies/*) render with Shopify's built-in markup; the title is
     injected in English and the theme can't change the text server-side, so map it here. */
  (function () {
    const h1 = document.querySelector('.shopify-policy__title h1');
    if (!h1) return;
    const map = {
      'refund policy': 'سياسة الاسترجاع',
      'privacy policy': 'سياسة الخصوصية',
      'terms of service': 'الشروط والأحكام',
      'shipping policy': 'سياسة الشحن',
      'legal notice': 'إشعار قانوني',
      'subscription policy': 'سياسة الاشتراك',
      'contact information': 'معلومات التواصل'
    };
    const ar = map[h1.textContent.trim().toLowerCase()];
    if (ar) { h1.textContent = ar; document.title = ar; }
  })();

  // Expose for downstream scripts (product-form.js, wishlist.js, collection.js).
  window.Balleh = Object.assign(Balleh, {
    openCart, openOverlay, closeAllOverlays, refreshCartDrawer, fetchCart, updateBadge,
    t: tr, formatMoney, toast, trapFocus, releaseFocus
  });
})();
